import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ConfigService } from "./config.service";
import { HmrcConfig } from "../models/gov-hmrc/hmrc-config";
import { OAuthAccessTokenResponse } from "../models/account/oauth-access-token-response";
import { OAuthGetAccessTokenPost } from "../models/account/oauth-get-access-token";
import { HmrcAuth } from "../models/gov-hmrc/hmrc-auth";
import { CreateGovHmrcAuthPost } from "../models/gov-hmrc/create-gov-hmrc-auth-post";
import { WindowService } from "./window.service";
import { tap } from "rxjs";

@Injectable()
export class GovHmrcService {
    config: HmrcConfig;

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private windowService: WindowService,
    ) {}

    generateHeaders(organisationReference: string) {
        return new HttpHeaders({
            "Gov-Client-Device-ID": this.generateDeviceId(),
            "Gov-Client-Screens": this.getGovClientScreensHeader(),
            "Gov-Client-Timezone": this.getGovClientTimezone(),
            "Gov-Client-Browser-JS-User-Agent": navigator.userAgent,
            "Gov-Client-Window-Size": `width=${window.innerWidth}&height=${window.innerHeight}`,
            "Gov-Client-User-IDs": this.getGovClientUserIds(
                organisationReference,
            ),
        });
    }

    testFraudPreventionHeader(organisationReference: string) {
        return this.http.post(
            `${this.configService.baseUrl}/gov-hmrc/test-fraud-prevention-header`,
            null,
            { headers: this.generateHeaders(organisationReference) },
        );
    }

    private generateDeviceId(): string {
        let id = localStorage.getItem("hmrc-device-id");
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem("hmrc-device-id", id);
        }
        return id;
    }

    getGovClientScreensHeader(): string {
        const screen = window.screen;
        const width = screen.width;
        const height = screen.height;
        const scaling = window.devicePixelRatio || 1;
        const colorDepth = screen.colorDepth;
        return `width=${width}&height=${height}&scaling-factor=${scaling}&colour-depth=${colorDepth}`;
    }

    getGovClientTimezone(): string {
        const offsetMinutes = new Date().getTimezoneOffset(); // minutes behind UTC
        const sign = offsetMinutes <= 0 ? "+" : "-";
        const absoluteMinutes = Math.abs(offsetMinutes);
        const hours = Math.floor(absoluteMinutes / 60)
            .toString()
            .padStart(2, "0");
        const minutes = (absoluteMinutes % 60).toString().padStart(2, "0");

        return `UTC${sign}${hours}:${minutes}`;
    }

    getGovClientUserIds(organisationReference: string) {
        const entries = [[`organisationReference`, organisationReference]];
        return entries
            .map(
                ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
            )
            .join("&");
    }

    getConfig() {
        return this.http
            .get<HmrcConfig>(`${this.configService.baseUrl}/gov-hmrc/config`)
            .pipe(tap((config) => this.setConfig(config)));
    }

    getOrganisationAuths(organisationReference: string) {
        return this.http.get<HmrcAuth[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths`,
        );
    }

    setAccessTokenWithOAuthCode(model: OAuthGetAccessTokenPost) {
        return this.http.post<OAuthAccessTokenResponse>(
            `${this.configService.baseUrl}/gov-hmrc/set-access-token`,
            model,
        );
    }

    createAuth(organisationReference: string, model: CreateGovHmrcAuthPost) {
        return this.http.post<HmrcAuth>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/create-auth`,
            model,
        );
    }

    listAllBusinesses(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/list-all-businesses`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveBusinessDetails(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        testScenario: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/retrieve-business-details/${businessId}`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createAndAmendQuarterlyPeriodTypeForBusiness(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        testScenario: string,
        quarterlyPeriodType: string,
    ) {
        let params = new HttpParams();
        params = params.set("quarterlyPeriodType", quarterlyPeriodType);
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.put(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/create-and-amend-quarterly-period-type-for-business/${businessId}`,
            null,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    setConfig(config: HmrcConfig) {
        this.config = config;
    }

    startOAuthFlow(authGuid: string, scopes: string[]) {
        let newWindow: Window;
        if (!this.config) {
            this.getConfig().subscribe((_) => {
                const oAuthLink = `${this.config.authorizeUri}?response_type=code&client_id=${this.config.clientId}&scope=${encodeURIComponent(scopes.join(" "))}&redirect_uri=${encodeURIComponent(this.config.redirectUri)}&state=${authGuid}`;
                newWindow = this.windowService.open(oAuthLink);
                newWindow.focus();
            });
        } else {
            const oAuthLink = `${this.config.authorizeUri}?response_type=code&client_id=${this.config.clientId}&scope=${encodeURIComponent(scopes.join(" "))}&redirect_uri=${encodeURIComponent(this.config.redirectUri)}&state=${authGuid}`;
            newWindow = this.windowService.open(oAuthLink);
            newWindow.focus();
        }
    }

    getIncomeAndExpenditure(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/obligations/get-income-and-expenditure`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    getCrystallisation(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/obligations/get-crystallisation`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    getEndOfPeriodStatement(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/obligations/get-end-of-period-statement`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    triggerIndividualCalculation(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
        model: any,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.post(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individual-calculations/trigger`,
            model,
            {
                responseType: "text",
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listSelfAssessmentTaxCalculationsOld(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
        taxYear: string,
    ) {
        let params = new HttpParams();
        params = params.set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individual-calculations/list-self-assessment-tax-calculations-old`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listSelfAssessmentTaxCalculations(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
        taxYear: string,
        calculationType: string,
    ) {
        let params = new HttpParams();
        params = params.set("taxYear", taxYear);
        params = params.set("calculationType", calculationType);
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individual-calculations/list-self-assessment-tax-calculations`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveSelfAssessmentTaxCalculation(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
        taxYear: string,
        calculationId: string,
    ) {
        let params = new HttpParams();
        params = params.set("taxYear", taxYear);
        params = params.set("calculationId", calculationId);
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individual-calculations/retrieve-self-assessment-tax-calculation`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    submitSelfAssessmentFinalDeclaration(
        organisationReference: string,
        authGuid: string,
        testScenario: string,
        model: any,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);
        return this.http.post(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individual-calculations/submit-self-assessment-final-declaration`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        businessId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("businessId", businessId);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-uk-property-business-annual-submission`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createAndAmendUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        businessId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("businessId", businessId);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-and-amend-uk-property-business-annual-submission`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        businessId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("businessId", businessId);

        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.post<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-uk-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        businessId: string,
        submissionId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("businessId", businessId)
            .set("submissionId", submissionId);

        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-uk-property-income-and-expenses-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        businessId: string,
        submissionId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("businessId", businessId)
            .set("submissionId", submissionId);

        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/amend-uk-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveUkPropertyCumulativePeriodSummary(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        businessId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("businessId", businessId);

        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-uk-property-cumulative-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createOrAmendUkPropertyCumulativePeriodSummary(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        businessId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("businessId", businessId);

        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-or-amend-uk-property-cumulative-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createAndAmendHistoricFhlUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-or-amend-fhl-uk-property-business-annual-submission`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveHistoricFhlUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-fhl-uk-property-business-annual-submission`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    deleteHistoricFhlUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/delete-fhl-uk-property-business-annual-submission`,
            {},
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createAndAmendHistoricNonFhlUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-or-amend-non-fhl-uk-property-business-annual-submission`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveHistoricNonFhlUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-non-fhl-uk-property-business-annual-submission`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    deleteHistoricNonFhlUkPropertyBusinessAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/delete-non-fhl-uk-property-business-annual-submission`,
            {},
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listHistoricFhlUkPropertyIncomeAndExpensesPeriodSummaries(
        organisationReference: string,
        authGuid: string,
        testScenario?: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/list-historic-fhl-uk-property-income-and-expenses-period-summaries`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createHistoricFhlUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-historic-fhl-uk-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendHistoricFhlUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        periodId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("periodId", periodId);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/amend-historic-fhl-uk-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveHistoricFhlUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        periodId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("periodId", periodId);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-historic-fhl-uk-property-income-and-expenses-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listHistoricNonFhlUkPropertyIncomeAndExpensesPeriodSummaries(
        organisationReference: string,
        authGuid: string,
        testScenario?: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/list-historic-non-fhl-uk-property-income-and-expenses-period-summaries`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createHistoricNonFhlUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams();
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-historic-non-fhl-uk-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendHistoricNonFhlUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        periodId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("periodId", periodId);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/amend-historic-non-fhl-uk-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveHistoricNonFhlUkPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        periodId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("periodId", periodId);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-historic-non-fhl-uk-property-income-and-expenses-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createForeignPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);
        if (testScenario) params = params.set("testScenario", testScenario);

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-foreign-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveForeignPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        submissionId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear)
            .set("submissionId", submissionId);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-foreign-property-income-and-expenses-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendForeignPropertyIncomeAndExpensesPeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        submissionId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear)
            .set("submissionId", submissionId);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/amend-foreign-property-income-and-expenses-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveForeignPropertyCumulativePeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-foreign-property-cumulative-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createOrAmendForeignPropertyCumulativePeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-or-amend-foreign-property-cumulative-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveForeignPropertyAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/retrieve-foreign-property-annual-submission`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createAndAmendForeignPropertyAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/create-and-amend-foreign-property-annual-submission`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    deletePropertyAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.delete<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/delete-property-annual-submission`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listPropertyIncomeAndExpensesPeriodSummaries(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/property-business/list-property-income-and-expenses-period-summaries`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveSelfEmploymentBusinessSourceAdjustableSummary(
        organisationReference: string,
        authGuid: string,
        calculationId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("calculationId", calculationId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/retrieve-self-employment-business-source-adjustable-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    submitSelfEmploymentAccountingAdjustments(
        organisationReference: string,
        authGuid: string,
        calculationId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("calculationId", calculationId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/submit-self-employment-accounting-adjustments`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveUkPropertyBusinessSourceAdjustableSummary(
        organisationReference: string,
        authGuid: string,
        calculationId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("calculationId", calculationId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/retrieve-uk-property-business-source-adjustable-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    submitUkPropertyAccountingAdjustments(
        organisationReference: string,
        authGuid: string,
        calculationId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("calculationId", calculationId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/submit-uk-property-business-accounting-adjustments`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveForeignPropertyBusinessSourceAdjustableSummary(
        organisationReference: string,
        authGuid: string,
        calculationId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("calculationId", calculationId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/retrieve-foreign-property-business-source-adjustable-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    submitForeignPropertyAccountingAdjustments(
        organisationReference: string,
        authGuid: string,
        calculationId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("calculationId", calculationId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/submit-foreign-property-business-accounting-adjustments`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listBusinessSourceAdjustableSummaries(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        typeOfBusiness?: string,
        businessId?: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/list-business-source-adjustable-summaries`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    triggerBusinessSourceAdjustableSummary(
        organisationReference: string,
        authGuid: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams();
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/business-source-adjustable-summary/trigger-business-source-adjustable-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createBroughtForwardLoss(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("taxYear", taxYear);
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/create-brought-forward-loss`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendBroughtForwardLoss(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        lossId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("lossId", lossId);
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/amend-brought-forward-loss`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listBroughtForwardLosses(
        organisationReference: string,
        authGuid: string,
        taxYearBroughtForwardFrom: string,
        businessId?: string,
        typeOfLoss?: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set(
            "taxYearBroughtForwardFrom",
            taxYearBroughtForwardFrom,
        );
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }
        if (businessId) {
            params = params.set("businessId", businessId);
        }
        if (testScenario) {
            params = params.set("typeOfLoss", typeOfLoss);
        }

        return this.http.get<any[]>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/list-brought-forward-losses`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveBroughtForwardLoss(
        organisationReference: string,
        authGuid: string,
        lossId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("lossId", lossId);
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/retrieve-brought-forward-loss`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    deleteBroughtForwardLoss(
        organisationReference: string,
        authGuid: string,
        taxYear: string,
        lossId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("taxYear", taxYear)
            .set("lossId", lossId);
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.delete<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/delete-brought-forward-loss`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createLossClaim(
        organisationReference: string,
        authGuid: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams();
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/create-loss-claim`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listLossClaims(
        organisationReference: string,
        authGuid: string,
        taxYearClaimedFor: string,
        businessId?: string,
        typeOfLoss?: string,
        typeOfClaim?: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set(
            "taxYearClaimedFor",
            taxYearClaimedFor,
        );
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }
        if (businessId) {
            params = params.set("businessId", businessId);
        }
        if (typeOfLoss) {
            params = params.set("typeOfLoss", typeOfLoss);
        }
        if (typeOfClaim) {
            params = params.set("typeOfClaim", typeOfClaim);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/list-loss-claims`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveLossClaim(
        organisationReference: string,
        authGuid: string,
        claimId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("claimId", claimId,);
        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/retrieve-loss-claim`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    deleteLossClaim(
        organisationReference: string,
        authGuid: string,
        claimId: string,
        taxYearClaimedFor: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("claimId", claimId)
            .set("taxYearClaimedFor", taxYearClaimedFor);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.delete<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/delete-loss-claim`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendLossClaimType(
        organisationReference: string,
        authGuid: string,
        claimId: string,
        taxYearClaimedFor: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("claimId", claimId)
            .set("taxYearClaimedFor", taxYearClaimedFor);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/amend-loss-claim-type`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendLossClaimsOrder(
        organisationReference: string,
        authGuid: string,
        taxYearClaimedFor: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams().set(
            "taxYearClaimedFor",
            taxYearClaimedFor,
        );

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/individuals-losses/amend-loss-claims-order`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createAndAmendSelfEmploymentAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/create-and-amend-self-employment-annual-submission`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveSelfEmploymentAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/retrieve-self-employment-annual-submission`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    deleteSelfEmploymentAnnualSubmission(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.delete<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/delete-self-employment-annual-submission`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createSelfEmploymentPeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams().set("businessId", businessId);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.post<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/create-self-employment-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    listSelfEmploymentPeriodSummaries(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/list-self-employment-period-summaries`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    amendSelfEmploymentPeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        periodId: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear)
            .set("periodId", periodId);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/amend-self-employment-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveSelfEmploymentPeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        periodId: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear)
            .set("periodId", periodId);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/retrieve-self-employment-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    createOrAmendSelfEmploymentCumulativePeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        model: any,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.put<void>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/create-or-amend-self-employment-cumulative-period-summary`,
            model,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }

    retrieveSelfEmploymentCumulativePeriodSummary(
        organisationReference: string,
        authGuid: string,
        businessId: string,
        taxYear: string,
        testScenario?: string,
    ) {
        let params = new HttpParams()
            .set("businessId", businessId)
            .set("taxYear", taxYear);

        if (testScenario) {
            params = params.set("testScenario", testScenario);
        }

        return this.http.get<any>(
            `${this.configService.baseUrl}/gov-hmrc/${organisationReference}/auths/${authGuid}/self-employment-business/retrieve-self-employment-cumulative-period-summary`,
            {
                params,
                headers: this.generateHeaders(organisationReference),
            },
        );
    }
}
